import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Grid,
  Header,
  Image,
  Segment,
  Message,
  Loader,
  Button,
  Divider,
  Icon,
  Label,
  List,
  Card
} from 'semantic-ui-react';
import {
  fetchApplication,
  updateApplication,
  selectSelectedApplication,
  selectApplicationsStatus,
  selectApplicationsError
} from '../../../slices/applicationSlice';
import { format } from 'date-fns';
import { HeaderAndPage } from '../../../common/components';
import microphoneStand from '../../../assets/microphone-stand.svg';
import { AuthStdFooter } from "../../../utils/auth-std-footer";
import DashBoardSideBar from '../../../utils/dashboard-sidebar';
import { ReactComponent as GearsAndBook } from '../../../assets/gearsAndBook.svg';
import { signS3UrlForUser } from '../../../utils/fetchSignedUrlByFolderV2';
import voiceActressDefault from '../../../assets/voiceActress.png';
import useSignedAvatar from '../../../hooks/useSignedAvatar';

// Character Avatar Component with S3 URL signing
const CharacterAvatar = ({ avatarUrl, characterName, userId }) => {
  const { signedUrl } = useSignedAvatar(avatarUrl, userId, voiceActressDefault);

  return (
    <img
      src={signedUrl}
      alt={characterName || 'Character Avatar'}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }}
      onError={(e) => {
        e.target.src = voiceActressDefault;
      }}
    />
  );
};

/**
 * ApplicationDetails component displays the details of a specific application
 */
const ApplicationDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get application from Redux store
  const application = useSelector(selectSelectedApplication);
  const status = useSelector(selectApplicationsStatus);
  const error = useSelector(selectApplicationsError);

  // Get the current user - use correct path to user data
  const currentUser = useSelector(state => state.user.profile?.data);

  // State for status update
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // State and refs for audio playback
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioRefs = useRef({});

  // State for signed sample URLs
  const [signedSampleUrls, setSignedSampleUrls] = useState({});

  // State for signed profile picture URL
  const [signedProfilePictureUrl, setSignedProfilePictureUrl] = useState(null);

  // State for signed book cover URL
  const [signedBookCoverUrl, setSignedBookCoverUrl] = useState(null);

  // Fetch application on component mount
  useEffect(() => {
    if (id) {
      dispatch(fetchApplication(id));
    }
  }, [dispatch, id]);

  // Optional: Keep minimal debug logging (can be removed in production)
  useEffect(() => {
    if (application) {
      console.log('[ApplicationDetails] Application loaded with actor data:', {
        actorUuid: application.actor?.uuid,
        actorName: application.actor?.firstName + ' ' + application.actor?.lastName,
        profilePictureUrl: application.actor?.profilePictureUrl,
        hasProfilePicture: !!application.actor?.profilePictureUrl
      });
      if (application.samples?.length > 0) {
        console.log(`[ApplicationDetails] Loaded ${application.samples.length} voice samples`);
      }
    }
  }, [application]);

  // Sign actor's profile picture URL
  useEffect(() => {
    const signProfilePicture = async () => {
      if (application?.actor?.profilePictureUrl && currentUser?.userId) {
        try {
          console.log('[ApplicationDetails] Signing profile picture URL:', application.actor.profilePictureUrl);
          const signedUrl = await signS3UrlForUser(currentUser.userId, application.actor.profilePictureUrl);
          setSignedProfilePictureUrl(signedUrl);
          console.log('[ApplicationDetails] Profile picture URL signed successfully');
        } catch (error) {
          console.error('Failed to sign profile picture URL:', error);
        }
      } else {
        console.log('[ApplicationDetails] Profile picture signing skipped:', {
          hasProfilePictureUrl: !!application?.actor?.profilePictureUrl,
          hasCurrentUserId: !!currentUser?.userId,
          profilePictureUrl: application?.actor?.profilePictureUrl
        });
      }
    };
    signProfilePicture();
  }, [application?.actor?.profilePictureUrl, currentUser?.userId]);

  // Sign book cover URL
  useEffect(() => {
    const signBookCover = async () => {
      if (application?.book?.coverUrl && currentUser?.userId) {
        try {
          const signedUrl = await signS3UrlForUser(currentUser.userId, application.book.coverUrl);
          setSignedBookCoverUrl(signedUrl);
        } catch (error) {
          console.error('Failed to sign book cover URL:', error);
        }
      }
    };
    signBookCover();
  }, [application?.book?.coverUrl, currentUser?.userId]);

  // Sign S3 URLs for audio samples
  useEffect(() => {
    const signSampleUrls = async () => {
      if (application?.samples && application.samples.length > 0 && currentUser?.userId) {
        const signedUrls = {};

        for (const sample of application.samples) {
          // Backend now returns voiceS3Url at root level consistently
          const urlToSign = sample.voiceS3Url;

          if (urlToSign) {
            try {
              const signedUrl = await signS3UrlForUser(currentUser.userId, urlToSign);
              signedUrls[sample.uuid || sample.id] = signedUrl;
            } catch (error) {
              console.error('Failed to sign URL for sample:', sample.name, error);
            }
          }
        }

        setSignedSampleUrls(signedUrls);
      }
    };

    signSampleUrls();
  }, [application?.samples, currentUser?.userId]);

  // Handle audio playback toggle
  const handleAudioToggle = (sampleId) => {
    // Pause all other audio elements
    Object.keys(audioRefs.current).forEach(id => {
      if (id !== sampleId && audioRefs.current[id]) {
        audioRefs.current[id].pause();
      }
    });

    const audio = audioRefs.current[sampleId];
    if (audio) {
      if (currentlyPlaying === sampleId) {
        // Currently playing this sample, pause it
        audio.pause();
        setCurrentlyPlaying(null);
      } else {
        // Play this sample
        audio.play().catch(error => {
          console.error('Failed to play audio:', error);
        });
        setCurrentlyPlaying(sampleId);
      }
    }
  };

  // Format duration from seconds to mm:ss
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if the current user is the creator of the opportunity
  const isCreator = application?.opportunity?.creator?.userId === currentUser?.userId ||
    application?.book?.creator?.userId === currentUser?.userId;

  console.log('[ApplicationDetails] Auth check:', {
    currentUserId: currentUser?.userId,
    oppCreatorUserId: application?.opportunity?.creator?.userId,
    bookCreatorUserId: application?.book?.creator?.userId,
    isCreator,
    actorUuid: application?.actor?.uuid,
    actorUserId: application?.actor?.userId
  });

  // Check if the current user is the applicant
  // Fix: Use userId instead of actorId (same as OpportunityDetails fix)
  const isApplicant = application?.actor?.uuid === currentUser?.userId ||
    application?.actor?.userId === currentUser?.userId;

  // Handle accept application button click
  const handleAcceptApplication = () => {
    setStatusUpdateLoading(true);

    dispatch(updateApplication({
      applicationId: id,
      applicationData: { status: 'Accepted' }
    }))
      .unwrap()
      .then(() => {
        // Re-fetch full application to restore creator info (update response doesn't include it)
        dispatch(fetchApplication(id));
        setStatusUpdateLoading(false);
      })
      .catch(error => {
        console.error('Failed to accept application:', error);
        setStatusUpdateLoading(false);
      });
  };

  // Handle reject application button click
  const handleRejectApplication = () => {
    setStatusUpdateLoading(true);

    dispatch(updateApplication({
      applicationId: id,
      applicationData: { status: 'Rejected' }
    }))
      .unwrap()
      .then(() => {
        // Re-fetch full application to restore creator info (update response doesn't include it)
        dispatch(fetchApplication(id));
        setStatusUpdateLoading(false);
      })
      .catch(error => {
        console.error('Failed to reject application:', error);
        setStatusUpdateLoading(false);
      });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return format(new Date(dateString), 'MMMM d, yyyy');
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Accepted':
        return 'green';
      case 'Rejected':
        return 'red';
      case 'Pending':
        return 'yellow';
      default:
        return 'grey';
    }
  };

  // Dashboard sidebar menu handler
  const handleMenuSelection = (menuId) => {
    switch (menuId) {
      case 'MY-ASSIGNMENTS':
        navigate('/dashboard', { state: { menuSelection: 'MY-ASSIGNMENTS' } });
        break;
      default:
        navigate('/dashboard', { state: { menuSelection: menuId } });
        break;
    }
  };

  // Wrapper to add dashboard sidebar layout around content
  const withDashboardSidebar = (content) => (
    <div className="std-pg-main-area std-pg-grey-background-color" style={{ marginBottom: "70px" }}>
      <div className='dashBoardSidebar-inner'>
        <div className="dashSliderTitle">Dashboard</div>
        <GearsAndBook className="dashboardHeadingIcon" />
        {DashBoardSideBar(handleMenuSelection)}
      </div>
      <div className="dashBoardSubPage">
        {content}
      </div>
      <div className='dashBoardSidebar-ext' />
    </div>
  );

  // Render loading state
  if (status === 'loading' && !application) {
    return withDashboardSidebar(
      <HeaderAndPage
        headerImage={microphoneStand}
        headerText="Application Details"
        subHeaderText="Loading application information..."
      >
        <Segment basic textAlign="center" style={{ minHeight: '300px' }}>
          <Loader active size="large">Loading Application</Loader>
        </Segment>
      </HeaderAndPage>
    );
  }

  // Render error state
  if (status === 'failed') {
    return withDashboardSidebar(
      <HeaderAndPage
        headerImage={microphoneStand}
        headerText="Application Details"
        subHeaderText="Error loading application information"
      >
        <Message negative>
          <Message.Header>Error Loading Application</Message.Header>
          <p>{error}</p>
          <Button as={Link} to="/opportunities" primary>
            <Icon name="arrow left" /> Back to Opportunities
          </Button>
        </Message>
      </HeaderAndPage>
    );
  }

  // Render not found state
  if (!application) {
    return withDashboardSidebar(
      <HeaderAndPage
        headerImage={microphoneStand}
        headerText="Application Details"
        subHeaderText="Application not found"
      >
        <Message warning>
          <Message.Header>Application Not Found</Message.Header>
          <p>The application you are looking for does not exist or has been removed.</p>
          <Button as={Link} to="/opportunities" primary>
            <Icon name="arrow left" /> Back to Opportunities
          </Button>
        </Message>
      </HeaderAndPage>
    );
  }

  // Render unauthorized state
  if (!isCreator && !isApplicant) {
    return withDashboardSidebar(
      <HeaderAndPage
        headerImage={microphoneStand}
        headerText="Application Details"
        subHeaderText="Unauthorized"
      >
        <Message negative>
          <Message.Header>Unauthorized</Message.Header>
          <p>You do not have permission to view this application.</p>
          <Button as={Link} to="/opportunities" primary>
            <Icon name="arrow left" /> Back to Opportunities
          </Button>
        </Message>
      </HeaderAndPage>
    );
  }

  // Get the default profile image
  const defaultProfileImage = 'https://react.semantic-ui.com/images/avatar/large/matthew.png';

  // Render application details
  return withDashboardSidebar(
    <HeaderAndPage
      headerImage={microphoneStand}
      headerText="Application Details"
      subHeaderText={isCreator ? 'Review application from voice actor' : 'Your application for voice opportunity'}
    >
      <Segment style={{ marginBottom: "70px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={10}>
              <Header as="h2">
                Application for {application.opportunity?.title || 'Opportunity'}
              </Header>
              <Label color={getStatusColor(application.status)} size="large">
                {application.status}
              </Label>
              <div style={{ marginTop: '10px' }}>
                <Icon name="calendar" /> Submitted: {formatDate(application.submissionDate)}
              </div>
            </Grid.Column>

            <Grid.Column width={4} textAlign="right">
              {isCreator && application.status === 'Pending' && (
                <div>
                  <Button
                    color="green"
                    onClick={handleAcceptApplication}
                    loading={statusUpdateLoading}
                    disabled={statusUpdateLoading}
                  >
                    <Icon name="check" /> Accept
                  </Button>
                  <Button
                    color="red"
                    onClick={handleRejectApplication}
                    loading={statusUpdateLoading}
                    disabled={statusUpdateLoading}
                    style={{ marginTop: '10px' }}
                  >
                    <Icon name="times" /> Reject
                  </Button>
                </div>
              )}
              {isCreator && (
                <Button
                  as={Link}
                  to={`/opportunities/${application.opportunity?.uuid}/applications`}
                  style={{ marginTop: '10px' }}
                >
                  <Icon name="list" /> All Applications
                </Button>
              )}
            </Grid.Column>
            <Grid.Column width={2}>
              {/* Book Cover Image */}
              {(signedBookCoverUrl || application.book?.coverUrl) && (
                <Image
                  src={signedBookCoverUrl || application.book?.coverUrl}
                  size="small"
                  rounded
                  style={{ maxHeight: '150px', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />

        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={6}>
              <Segment>
                <Header as="h3">Applicant Information</Header>
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={6} textAlign="center">
                      <div style={{
                        width: '120px',
                        height: '120px',
                        margin: '0 auto',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid #ddd',
                        backgroundColor: '#f5f5f5'
                      }}>
                        <img
                          src={signedProfilePictureUrl || application.actor?.profilePictureUrl || defaultProfileImage}
                          alt="Profile"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.src = defaultProfileImage;
                          }}
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column width={10}>
                      <List>
                        <List.Item>
                          <List.Icon name="user" />
                          <List.Content>
                            <List.Header>
                              {(() => {
                                if (!application.actor) return 'Unknown Actor';
                                // Try different name field combinations
                                if (application.actor.firstName || application.actor.lastName) {
                                  const firstName = application.actor.firstName || '';
                                  const lastName = application.actor.lastName || '';
                                  return `${firstName} ${lastName}`.trim() || 'Unknown Actor';
                                }
                                if (application.actor.name) {
                                  return application.actor.name;
                                }
                                if (application.actor.title) {
                                  return application.actor.title;
                                }
                                return 'Unknown Actor';
                              })()}
                            </List.Header>
                          </List.Content>
                        </List.Item>
                        {application.actor?.email && (
                          <List.Item>
                            <List.Icon name="mail" />
                            <List.Content>{application.actor.email}</List.Content>
                          </List.Item>
                        )}
                        {application.actor?.phone && (
                          <List.Item>
                            <List.Icon name="phone" />
                            <List.Content>{application.actor.phone}</List.Content>
                          </List.Item>
                        )}
                      </List>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
            </Grid.Column>

            <Grid.Column width={10}>
              <Segment style={{ position: 'relative' }}>
                {/* Book Cover in top right corner */}
                {/* {(signedBookCoverUrl || application.book?.coverUrl) && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '80px',
                    height: '120px',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <img
                      src={signedBookCoverUrl || application.book?.coverUrl}
                      alt="Book Cover"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                      onError={(e) => {
                        e.target.parentElement.style.display = 'none';
                      }}
                    />
                  </div>
                )} */}
                <Header as="h3">Opportunity Information</Header>
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <List>
                        <List.Item>
                          <List.Icon name="book" />
                          <List.Content>
                            <List.Header>Book</List.Header>
                            <List.Description>
                              {application.book?.name || 'Unknown Book'}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                        <List.Item>
                          <List.Icon name="user circle" />
                          <List.Content>
                            <List.Header>Book Creator</List.Header>
                            <List.Description>
                              {application.book?.creator?.name || 'Unknown Creator'}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                        <List.Item>
                          <List.Icon name="pencil" />
                          <List.Content>
                            <List.Header>Author</List.Header>
                            <List.Description>
                              {application.book?.author?.name || 'Unknown Author'}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                        <List.Item>
                          <List.Icon name="user" />
                          <List.Content>
                            <List.Header>Character</List.Header>
                            <List.Description>
                              {application.character?.name || 'Unknown Character'}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                      </List>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <List>
                        <List.Item>
                          <List.Icon name="dollar" />
                          <List.Content>
                            <List.Header>Compensation</List.Header>
                            <List.Description>
                              {application.opportunity?.compensation || 'Not specified'}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                        <List.Item>
                          <List.Icon name="calendar" />
                          <List.Content>
                            <List.Header>Deadline</List.Header>
                            <List.Description>
                              {application.opportunity?.deadline
                                ? formatDate(application.opportunity.deadline)
                                : 'No deadline'}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                      </List>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              <Segment>
                <Header as="h3">Application Notes</Header>
                <p style={{ whiteSpace: 'pre-line' }}>{application.notes || 'No notes provided'}</p>
              </Segment>
            </Grid.Column>
          </Grid.Row>

          {/* Voice Samples Section - Check both 'samples' and 'sampleIds' */}
          {((application.samples && application.samples.length > 0) ||
            (application.sampleIds && application.sampleIds.length > 0)) && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Segment>
                    <Header as="h3">
                      <Icon name="microphone" />
                      Voice Samples Submitted
                    </Header>
                    <p style={{ marginBottom: '20px' }}>
                      {application.samples && application.samples.length > 0
                        ? `${application.samples.length} voice sample${application.samples.length !== 1 ? 's' : ''} included with this application`
                        : `Voice samples submitted for review`}
                    </p>

                    {/* If we have populated samples, show them with enhanced UI */}
                    {application.samples && application.samples.length > 0 ? (
                      <div className="voice-samples-display-section">
                        <Grid columns={1} stackable>
                          {application.samples.map(sample => {
                            const sampleId = sample.uuid || sample.id;
                            const signedUrl = signedSampleUrls[sampleId];

                            return (
                              <Grid.Column key={sampleId}>
                                <Segment className="voice-sample-card-enhanced">
                                  <Grid>
                                    <Grid.Row>
                                      {/* Avatar Column */}
                                      <Grid.Column width={2} textAlign="center">
                                        <div style={{
                                          width: '80px',
                                          height: '80px',
                                          borderRadius: '50%',
                                          overflow: 'hidden',
                                          margin: '0 auto',
                                          border: '2px solid #ddd',
                                          backgroundColor: '#f5f5f5'
                                        }}>
                                          {sample.avatarUrl ? (
                                            <CharacterAvatar
                                              avatarUrl={sample.avatarUrl}
                                              characterName={sample.name}
                                              userId={currentUser?.userId}
                                            />
                                          ) : (
                                            <img
                                              src={voiceActressDefault}
                                              alt="Default Avatar"
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                          )}
                                        </div>
                                      </Grid.Column>

                                      {/* Character Information Column */}
                                      <Grid.Column width={10}>
                                        <Header as="h4" style={{ marginBottom: '5px' }}>
                                          {sample.name}
                                          {sample.role && <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666' }}> - {sample.role}</span>}
                                        </Header>

                                        {/* Character Metadata */}
                                        <div style={{ marginBottom: '10px' }}>
                                          {sample.characterType && (
                                            <Label size="tiny" basic>
                                              <Icon name="users" />
                                              {sample.characterType}
                                            </Label>
                                          )}
                                          {sample.type && (
                                            <Label size="tiny" basic>
                                              <Icon name="tag" />
                                              {sample.type}
                                            </Label>
                                          )}
                                          {sample.genre && (
                                            <Label size="tiny" basic>
                                              <Icon name="book" />
                                              {sample.genre}
                                            </Label>
                                          )}
                                          {sample.gender && (
                                            <Label size="tiny" basic>
                                              <Icon name="venus mars" />
                                              {sample.gender}
                                            </Label>
                                          )}
                                          {(sample.age || sample.ageGroup) && (
                                            <Label size="tiny" basic>
                                              <Icon name="calendar outline" />
                                              {sample.age || sample.ageGroup}
                                            </Label>
                                          )}
                                        </div>

                                        {/* Description */}
                                        {sample.description && (
                                          <p style={{ fontSize: '0.9em', marginBottom: '10px', color: '#444' }}>
                                            {sample.description.length > 150
                                              ? `${sample.description.substring(0, 150)}...`
                                              : sample.description}
                                          </p>
                                        )}

                                        {/* Script Content */}
                                        {sample.textContent && (
                                          <div style={{
                                            backgroundColor: '#f8f8f8',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            marginTop: '10px'
                                          }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '0.85em', marginBottom: '5px', color: '#666' }}>
                                              Script Content
                                            </div>
                                            <p style={{
                                              fontSize: '0.85em',
                                              fontStyle: 'italic',
                                              margin: '5px 0 0 0',
                                              color: '#555'
                                            }}>
                                              "{sample.textContent.length > 200
                                                ? `${sample.textContent.substring(0, 200)}...`
                                                : sample.textContent}"
                                            </p>
                                          </div>
                                        )}
                                      </Grid.Column>

                                      {/* Audio Controls Column */}
                                      <Grid.Column width={4} textAlign="center">
                                        <div style={{ paddingTop: '20px' }}>
                                          <div style={{ fontSize: '0.85em', color: '#555', marginBottom: '10px', lineHeight: '1.2' }}>
                                            Voice Sample
                                          </div>
                                          {(() => {
                                            // Check for audio URL in multiple possible locations
                                            const hasAudioUrl = sample.voiceS3Url ||
                                              sample.s3Url ||
                                              sample.sample?.voiceS3Url ||
                                              sample.sample?.s3Url ||
                                              sample.audio?.s3Url ||
                                              sample.voiceUrl;

                                            if (signedUrl) {
                                              return (
                                                <>
                                                  <audio
                                                    ref={el => audioRefs.current[sampleId] = el}
                                                    src={signedUrl}
                                                    onEnded={() => setCurrentlyPlaying(null)}
                                                    style={{ display: 'none' }}
                                                  />
                                                  <Button
                                                    type="button"
                                                    icon={currentlyPlaying === sampleId ? "pause" : "play"}
                                                    circular
                                                    size="large"
                                                    primary
                                                    onClick={() => handleAudioToggle(sampleId)}
                                                    title={currentlyPlaying === sampleId ? "Pause" : "Play"}
                                                  />
                                                  {(sample.duration || sample.fileDuration) && (
                                                    <div style={{ marginTop: '10px', fontSize: '1.1em', fontWeight: 'bold' }}>
                                                      {formatDuration(sample.duration || sample.fileDuration)}
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            } else if (hasAudioUrl) {
                                              return (
                                                <>
                                                  <Button
                                                    type="button"
                                                    icon="play"
                                                    circular
                                                    size="large"
                                                    disabled
                                                    title="Loading audio..."
                                                  />
                                                  <Label size="small" basic style={{ marginTop: '10px' }}>
                                                    Loading...
                                                  </Label>
                                                </>
                                              );
                                            } else {
                                              return (
                                                <>
                                                  <Button
                                                    type="button"
                                                    icon="play"
                                                    circular
                                                    size="large"
                                                    disabled
                                                    title="No audio available"
                                                  />
                                                  <Label size="small" basic style={{ marginTop: '10px' }}>
                                                    No Audio
                                                  </Label>
                                                </>
                                              );
                                            }
                                          })()}
                                        </div>
                                      </Grid.Column>
                                    </Grid.Row>
                                  </Grid>
                                </Segment>
                              </Grid.Column>
                            );
                          })}
                        </Grid>
                      </div>
                    ) : application.sampleIds && application.sampleIds.length > 0 ? (
                      // If we only have IDs, show a message
                      <Message info>
                        <Message.Header>Voice Samples Submitted</Message.Header>
                        <p>
                          {application.sampleIds.length} voice sample{application.sampleIds.length !== 1 ? 's' : ''}
                          {application.sampleIds.length === 1 ? ' was' : ' were'} submitted with this application.
                        </p>
                        <p style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
                          Sample IDs: {application.sampleIds.join(', ')}
                        </p>
                        <p style={{ fontSize: '0.85em', color: '#999', marginTop: '10px', fontStyle: 'italic' }}>
                          Note: Sample details are not currently populated from the backend.
                          The voice actor submitted these samples for review.
                        </p>
                      </Message>
                    ) : null}
                  </Segment>
                </Grid.Column>
              </Grid.Row>
            )}
        </Grid>

        <Divider />

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {isCreator ? (
            <Button as={Link} to={`/opportunities/${application.opportunity?.uuid}`}>
              <Icon name="arrow left" /> Back to Opportunity
            </Button>
          ) : (
            <>
              <Button as={Link} to="/opportunities">
                <Icon name="arrow left" /> Back to Opportunities
              </Button>
              {application.status === 'Accepted' && (
                <Button
                  as={Link}
                  to={`/recording-studio/${application.uuid}`}
                  color="green"
                  style={{ marginLeft: '10px' }}
                >
                  <Icon name="microphone" /> Go to Recording Studio
                </Button>
              )}
            </>
          )}
        </div>
      </Segment>
      {AuthStdFooter()}
    </HeaderAndPage>
  );
};

export default ApplicationDetails;
